import React from 'react';

export interface SelectionActionsRowsProps {
  selectedCount: number;
  tagInput: string;
  setTagInput: (v: string) => void;
  onAddTags: () => void;
  addingTags: boolean;
  tagToRemove: string;
  setTagToRemove: (v: string) => void;
  selectedImagesTagsUnion: string[];
  onRemoveTag: () => void;
  removingTag: boolean;
  onUploadToPicsellia: () => void;
  activePicselliaUploadCount?: number;
  alreadyInPicselliaCount?: number;
  onDeselectAll: () => void;
  classificationBatchInput: string;
  setClassificationBatchInput: (v: string) => void;
  onAddClassificationsBatch: () => void;
  addingClassificationsBatch: boolean;
  classificationToRemove: string;
  setClassificationToRemove: (v: string) => void;
  selectedImagesClassificationsUnion: string[];
  onRemoveClassificationBatch: () => void;
  removingClassification: boolean;
  speciesInput: string;
  setSpeciesInput: (v: string) => void;
  onSetSpecies: () => void;
  settingSpecies: boolean;
  onClearSpecies: () => void;
  clearingSpecies: boolean;
  selectedImagesSpeciesUnion: string[];
  selectedWithLoadedDims: number;
  onSaveImageDims: () => void;
  savingImageDims: boolean;
}

const SelectionActionsRows: React.FC<SelectionActionsRowsProps> = ({
  selectedCount,
  tagInput,
  setTagInput,
  onAddTags,
  addingTags,
  tagToRemove,
  setTagToRemove,
  selectedImagesTagsUnion,
  onRemoveTag,
  removingTag,
  onUploadToPicsellia,
  activePicselliaUploadCount = 0,
  alreadyInPicselliaCount = 0,
  onDeselectAll,
  classificationBatchInput,
  setClassificationBatchInput,
  onAddClassificationsBatch,
  addingClassificationsBatch,
  classificationToRemove,
  setClassificationToRemove,
  selectedImagesClassificationsUnion,
  onRemoveClassificationBatch,
  removingClassification,
  speciesInput,
  setSpeciesInput,
  onSetSpecies,
  settingSpecies,
  onClearSpecies,
  clearingSpecies,
  selectedImagesSpeciesUnion,
  selectedWithLoadedDims,
  onSaveImageDims,
  savingImageDims,
}) => (
    <>
      {/* Row 3: Tags to add + actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <input
          type="text"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          placeholder="Tags to add (comma-separated)"
          style={{ padding: '0.5rem 0.75rem', border: '1px solid #dee2e6', borderRadius: '4px', width: '220px', maxWidth: '100%', fontSize: '0.9rem' }}
          onKeyDown={(e) => e.key === 'Enter' && onAddTags()}
        />
        <button
          onClick={onAddTags}
          disabled={addingTags || !tagInput.trim() || selectedCount === 0}
          style={{
            padding: '0.5rem 1rem',
            background: selectedCount > 0 ? '#007bff' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: addingTags || !tagInput.trim() || selectedCount === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {addingTags ? 'Adding...' : `Add tags (${selectedCount})`}
        </button>
        <select
          value={tagToRemove}
          onChange={(e) => setTagToRemove(e.target.value)}
          disabled={selectedCount === 0 || selectedImagesTagsUnion.length === 0}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            fontSize: '0.9rem',
            minWidth: '160px',
            background: selectedCount > 0 && selectedImagesTagsUnion.length > 0 ? '#fff' : '#f5f5f5',
          }}
          title={selectedCount === 0 ? 'Select images first' : selectedImagesTagsUnion.length === 0 ? 'Selected images have no tags' : 'Choose a tag to remove from all selected images'}
        >
          <option value="">
            {selectedCount === 0 ? 'Select images first' : selectedImagesTagsUnion.length === 0 ? 'No tags on selected' : 'Tag to remove...'}
          </option>
          {selectedImagesTagsUnion.map((tag) => (
            <option key={tag} value={tag}>{tag}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={onRemoveTag}
          disabled={removingTag || !tagToRemove.trim() || selectedCount === 0}
          style={{
            padding: '0.5rem 1rem',
            background: tagToRemove.trim() && selectedCount > 0 ? '#dc3545' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: removingTag || !tagToRemove.trim() || selectedCount === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {removingTag ? 'Removing...' : 'Remove tag'}
        </button>
        {alreadyInPicselliaCount > 0 && (
          <span style={{ fontSize: '0.9rem', color: '#666' }}>
            {alreadyInPicselliaCount} of {selectedCount} already in Picsellia
          </span>
        )}
        <button
          onClick={onUploadToPicsellia}
          disabled={selectedCount === 0}
          style={{
            padding: '0.5rem 1rem',
            background: selectedCount > 0 ? '#28a745' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedCount === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {`Send to Picsellia (${selectedCount})`}
        </button>
        {activePicselliaUploadCount > 0 && (
          <span style={{ fontSize: '0.85rem', color: '#0066cc', fontWeight: 500 }}>
            {activePicselliaUploadCount} uploading
          </span>
        )}
        <button
          type="button"
          onClick={onDeselectAll}
          disabled={selectedCount === 0}
          style={{
            padding: '0.5rem 1rem',
            background: selectedCount > 0 ? '#6c757d' : '#e9ecef',
            color: selectedCount > 0 ? 'white' : '#adb5bd',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            cursor: selectedCount === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          Deselect all
        </button>
      </div>

      {/* Row 4: Classifications (add + remove) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <input
          type="text"
          value={classificationBatchInput}
          onChange={(e) => setClassificationBatchInput(e.target.value)}
          placeholder="Classification (e.g. upright, vertical)"
          style={{ padding: '0.5rem 0.75rem', border: '1px solid #dee2e6', borderRadius: '4px', width: '200px', maxWidth: '100%', fontSize: '0.9rem' }}
          onKeyDown={(e) => e.key === 'Enter' && onAddClassificationsBatch()}
        />
        <button
          type="button"
          onClick={onAddClassificationsBatch}
          disabled={addingClassificationsBatch || !classificationBatchInput.trim() || selectedCount === 0}
          style={{
            padding: '0.5rem 1rem',
            background: selectedCount > 0 ? '#667eea' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: addingClassificationsBatch || !classificationBatchInput.trim() || selectedCount === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {addingClassificationsBatch ? 'Adding...' : `Add classification (${selectedCount})`}
        </button>
        <select
          value={classificationToRemove}
          onChange={(e) => setClassificationToRemove(e.target.value)}
          disabled={selectedCount === 0 || selectedImagesClassificationsUnion.length === 0}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            fontSize: '0.9rem',
            minWidth: '180px',
            background: selectedCount > 0 && selectedImagesClassificationsUnion.length > 0 ? '#fff' : '#f5f5f5',
          }}
          title={selectedCount === 0 ? 'Select images first' : selectedImagesClassificationsUnion.length === 0 ? 'Selected images have no classifications' : 'Choose a classification to remove from all selected images'}
        >
          <option value="">
            {selectedCount === 0 ? 'Select images first' : selectedImagesClassificationsUnion.length === 0 ? 'No classifications on selected' : 'Classification to remove...'}
          </option>
          {selectedImagesClassificationsUnion.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={onRemoveClassificationBatch}
          disabled={removingClassification || !classificationToRemove.trim() || selectedCount === 0}
          style={{
            padding: '0.5rem 1rem',
            background: classificationToRemove.trim() && selectedCount > 0 ? '#dc3545' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: removingClassification || !classificationToRemove.trim() || selectedCount === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {removingClassification ? 'Removing...' : 'Remove classification'}
        </button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <input
          type="text"
          value={speciesInput}
          onChange={(e) => setSpeciesInput(e.target.value)}
          placeholder="Set species (single value)"
          style={{ padding: '0.5rem 0.75rem', border: '1px solid #dee2e6', borderRadius: '4px', width: '220px', maxWidth: '100%', fontSize: '0.9rem' }}
          onKeyDown={(e) => e.key === 'Enter' && onSetSpecies()}
        />
        <button
          type="button"
          onClick={onSetSpecies}
          disabled={settingSpecies || !speciesInput.trim() || selectedCount === 0}
          style={{
            padding: '0.5rem 1rem',
            background: selectedCount > 0 ? '#198754' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: settingSpecies || !speciesInput.trim() || selectedCount === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {settingSpecies ? 'Saving...' : `Set species (${selectedCount})`}
        </button>
        <button
          type="button"
          onClick={onClearSpecies}
          disabled={clearingSpecies || selectedCount === 0}
          style={{
            padding: '0.5rem 1rem',
            background: selectedCount > 0 ? '#dc3545' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: clearingSpecies || selectedCount === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {clearingSpecies ? 'Clearing...' : 'Clear species'}
        </button>
        {selectedImagesSpeciesUnion.length > 0 && (
          <span style={{ fontSize: '0.85rem', color: '#666' }}>
            Current species on selected: {selectedImagesSpeciesUnion.join(', ')}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <button
          type="button"
          onClick={onSaveImageDims}
          disabled={savingImageDims || selectedWithLoadedDims === 0}
          style={{
            padding: '0.5rem 1rem',
            background: selectedWithLoadedDims > 0 ? '#0d6efd' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: savingImageDims || selectedWithLoadedDims === 0 ? 'not-allowed' : 'pointer',
          }}
          title={
            selectedWithLoadedDims === 0
              ? 'Select images that have finished loading in the grid'
              : 'Write naturalWidth/naturalHeight from the browser to Postgres img_width/img_height'
          }
        >
          {savingImageDims
            ? 'Saving dimensions...'
            : `Save image dimensions (${selectedWithLoadedDims})`}
        </button>
        {selectedCount > 0 && selectedWithLoadedDims < selectedCount && (
          <span style={{ fontSize: '0.85rem', color: '#666' }}>
            {selectedCount - selectedWithLoadedDims} selected image(s) still loading
          </span>
        )}
      </div>
    </>
);

export default SelectionActionsRows;
